import React, { useEffect, useRef } from 'react'
import styled from '@emotion/styled'
import Hidden from 'src/components/Hidden'
import { P1 } from 'src/components/Typography'

import { useTheme } from 'src/shared/themeContext'
import DesktopSteps from './DesktopSteps'

type ResponsiveStepsProps = {
  current: number
  steps: string[]
  desktopNote?: React.ReactNode
} & React.HTMLAttributes<HTMLDivElement>

const ResponsiveSteps = ({ current, steps, desktopNote }: ResponsiveStepsProps) => {
  const scrollableElement = useRef<HTMLDivElement>(null)
  const { brand, success, neutral } = useTheme()

  useEffect(() => {
    const node = scrollableElement.current

    if (node !== null) {
      node.scrollLeft = current * 120
    }
  }, [current])

  const MobileView = styled.div`
    height: 58px;
    padding-left: 16px;
    border-bottom: 1px solid ${neutral.border};
    max-width: 100vw;
    display: flex;
    align-items: center;
    overflow: auto;
    background: ${neutral.background};

    &::-webkit-scrollbar {
      display: none;
    }
  `

  const StepIcon = styled.div<{ current: number; index: number }>`
    width: 9px;
    height: 9px;
    border-radius: 50%;
    margin-right: 10px;
    background: ${neutral.emphasis};
    display: inline-block;

    ${props => {
      if (props.current === props.index) {
        return `
        background: ${brand.primary.default};
      `
      } else if (props.current > props.index) {
        return `
        background: ${success.emphasis};
      `
      } else {
        return ``
      }
    }}
  `

  const StepConnector = styled.div`
    width: 20px;
    margin-left: 10px;
    margin-right: 10px;
    height: 1px;
    background: ${neutral.emphasis};
    display: inline-block;
    margin-bottom: 4px;
  `

  const MobileSteps = styled.div`
    padding-right: 16px;
    width: 1px;
    white-space: nowrap;
  `

  const StepItem = styled(P1)<{ inactive: boolean; current: boolean }>`
    color: #1b2736;
    display: inline-flex;
    align-items: center;
    white-space: nowrap;

    ${props =>
      props.inactive &&
      `
    color: #777777;
  `}

    &:last-child {
      padding-right: 16px;
    }

    ${props =>
      props.current &&
      `
      font-weight: bold;
  `}
  `
  return (
    <>
      <Hidden only="sm">
        <div style={{ padding: '30px', width: '266px' }}>
          <DesktopSteps
            progressDot
            current={current}
            direction="vertical"
            style={{ marginBottom: '34px' }}
          >
            {steps.map((step, index) => (
              <DesktopSteps.Step key={`desktop-${step}-${index}`} title={step} />
            ))}
          </DesktopSteps>
          {desktopNote}
        </div>
      </Hidden>
      <Hidden only="lg" ref={scrollableElement}>
        <MobileView>
          <MobileSteps>
            {steps.map((step, index) => (
              <span key={`mobile-${step}-${index}`}>
                <StepItem inactive={current < index} current={current === index}>
                  <StepIcon index={index} current={current} />
                  {step}
                </StepItem>
                {index !== steps.length - 1 && <StepConnector />}
              </span>
            ))}
          </MobileSteps>
        </MobileView>
      </Hidden>
    </>
  )
}

export default ResponsiveSteps
